/**
 * TestMappingPage.tsx
 * Admin → Test Mapping (master-detail redesign, v1.0.34) — connect lab `Test`
 * rows to scheduling `TestCatalogItem` rows (the link that, when missing,
 * silently drops tests from schedule generation — the WO-2026-091 failure
 * class).
 *
 * Two-column master-detail (replaces the v1.0.32 React Flow canvas, which
 * was unreadable at real data volumes):
 *   TestListColumn   — searchable, filterable lab-test list (left, master)
 *   TestDetailPane   — one test's full mapping workspace (right, detail):
 *                      Autocomplete picker, suggested matches, confirm/notes/
 *                      unmap, ChainExplorerPanel recipe preview
 *   useTestMappingStudio — data + mutations (services/testMappingService)
 *   mappingModel     — pure list/status/stats logic (unit-tested)
 */

import { useMemo, useState } from 'react';
import {
  Alert, Box, Chip, CircularProgress, Paper, Typography, alpha, useTheme,
} from '@mui/material';
import { Hub } from '@mui/icons-material';
import { ConfirmDialog, CONFIRM_INITIAL, type ConfirmDialogState } from '../../../components/shared/ConfirmDialog';
import { PageHelpButton } from '../../../components/help/PageHelpButton';
import { TestListColumn } from './TestListColumn';
import { TestDetailPane } from './TestDetailPane';
import { useTestMappingStudio } from './useTestMappingStudio';
import {
  computeMappingStats,
  countByFilter,
  filterAndSortTests,
  type TestListFilter,
} from './mappingModel';
import type { MappingStudioTest } from '../../../services/testMappingService';

export const TestMappingPage = () => {
  const theme = useTheme();
  const studio = useTestMappingStudio();

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<TestListFilter>('all');
  const [selectedTestKey, setSelectedTestKey] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmDialogState>(CONFIRM_INITIAL);
  const [actionError, setActionError] = useState<string | null>(null);

  const tests = studio.data?.tests ?? [];
  const catalogItems = studio.data?.catalogItems ?? [];

  const stats = useMemo(() => computeMappingStats(tests), [tests]);
  const counts = useMemo(() => countByFilter(tests), [tests]);
  const listTests = useMemo(
    () => filterAndSortTests(tests, search, filter),
    [tests, search, filter],
  );

  const testByKey = useMemo(() => new Map(tests.map((t) => [t.TestKey, t])), [tests]);
  const catalogByKey = useMemo(
    () => new Map(catalogItems.map((c) => [c.TestCatalogItemKey, c])),
    [catalogItems],
  );

  const selectedTest = selectedTestKey ? testByKey.get(selectedTestKey) ?? null : null;
  const selectedSuggestion = selectedTestKey
    ? studio.suggestions.find((s) => s.testKey === selectedTestKey) ?? null
    : null;

  // ── Mapping actions (picker, suggestion-accept, unmap all land here) ────────

  const runAction = (action: () => Promise<void>) => {
    setActionError(null);
    action().catch((err) => {
      setActionError(err instanceof Error ? err.message : 'The mapping change failed — reload and retry.');
    });
  };

  /** Create or (after confirm) replace the mapping of a test. */
  const requestMapping = (testKey: string, testCatalogItemKey: string) => {
    const test = testByKey.get(testKey);
    const target = catalogByKey.get(testCatalogItemKey);
    if (!test || !target) return;
    if (test.mapping && test.mapping.TestCatalogItemKey === testCatalogItemKey) return; // no-op

    const doMap = () => runAction(() => studio.mapTest(testKey, testCatalogItemKey));

    if (test.mapping) {
      const currentName = catalogByKey.get(test.mapping.TestCatalogItemKey)?.TestName ?? 'its current catalog item';
      setConfirm({
        open: true,
        title: 'Replace existing mapping?',
        message: `“${test.TestName}” is currently mapped to “${currentName}”.\n\nReplace it with “${target.TestName}”? The new mapping starts unconfirmed until the lab blesses it.`,
        confirmLabel: 'Replace mapping',
        confirmColor: 'warning',
        onConfirm: doMap,
      });
    } else {
      doMap();
    }
  };

  const requestUnmap = (test: MappingStudioTest) => {
    if (!test.mapping) return;
    const mapKey = test.mapping.TestCatalogMapKey;
    const targetName = catalogByKey.get(test.mapping.TestCatalogItemKey)?.TestName ?? 'its catalog item';
    setConfirm({
      open: true,
      title: 'Unmap this test?',
      message: `Remove the mapping “${test.TestName}” → “${targetName}”?\n\nWork-order lines on this test (${test.woLineCount}) will stop generating schedule tasks until it is mapped again.`,
      confirmLabel: 'Unmap',
      confirmColor: 'error',
      onConfirm: () => runAction(() => studio.unmap(mapKey)),
    });
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  if (studio.loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Header: title + stats */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Hub sx={{ color: theme.palette.primary.main }} />
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
          Test Mapping
        </Typography>
        <Chip
          size="small"
          label={`${stats.mappedCount} of ${stats.totalTests} tests mapped`}
          sx={{
            fontWeight: 700,
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
          }}
        />
        {stats.unconfirmedCount > 0 && (
          <Chip
            size="small"
            clickable
            label={`${stats.unconfirmedCount} unconfirmed`}
            onClick={() => setFilter('unconfirmed')}
            sx={{
              fontWeight: 700,
              backgroundColor: alpha(theme.admin.mappingUnconfirmed, 0.14),
              color: theme.admin.mappingUnconfirmed,
            }}
          />
        )}
        {stats.unmappedCount > 0 && (
          <Chip
            size="small"
            clickable
            label={`${stats.unmappedWoLines} work-order line${stats.unmappedWoLines === 1 ? '' : 's'} affected by ${stats.unmappedCount} unmapped test${stats.unmappedCount === 1 ? '' : 's'}`}
            onClick={() => setFilter('unmapped')}
            sx={{
              fontWeight: 700,
              backgroundColor: alpha(theme.schedule.conflictSoft, 0.12),
              color: theme.schedule.conflictSoft,
            }}
          />
        )}
        <Box sx={{ flex: 1 }} />
        <PageHelpButton pageKey="test-mapping" />
      </Box>

      {(studio.error || actionError) && (
        <Alert severity="error" onClose={() => setActionError(null)}>
          {studio.error ?? actionError}
        </Alert>
      )}

      {/* Master-detail body */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', gap: 1.5 }}>
        <Paper
          variant="outlined"
          sx={{
            width: 380,
            flexShrink: 0,
            borderRadius: 2,
            borderColor: alpha(theme.palette.divider, 0.5),
            p: 1.5,
            minHeight: 0,
          }}
        >
          <TestListColumn
            tests={listTests}
            counts={counts}
            search={search}
            onSearchChange={setSearch}
            filter={filter}
            onFilterChange={setFilter}
            selectedTestKey={selectedTestKey}
            onSelect={setSelectedTestKey}
            catalogNameByKey={(key) => catalogByKey.get(key)?.TestName ?? null}
          />
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            borderRadius: 2,
            borderColor: alpha(theme.palette.divider, 0.5),
            overflow: 'hidden',
          }}
        >
          <TestDetailPane
            test={selectedTest}
            catalogItems={catalogItems}
            stats={stats}
            suggestion={selectedSuggestion}
            suggestionsLoading={studio.suggestionsLoading}
            onLoadSuggestions={studio.loadSuggestions}
            onPickCatalogItem={requestMapping}
            onUnmap={requestUnmap}
            onToggleConfirmed={(mapKey, isConfirmed) =>
              runAction(() => studio.setConfirmed(mapKey, isConfirmed))}
            onSaveNotes={(mapKey, notes) => runAction(() => studio.setNotes(mapKey, notes))}
          />
        </Paper>
      </Box>

      <ConfirmDialog state={confirm} onClose={() => setConfirm(CONFIRM_INITIAL)} />
    </Box>
  );
};
